import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import GetUser from "../utils/getUser";
import UseGraph from "../utils/useGraph";
import formQueryUrl from "../utils/formQueryUrl";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowsRotate,
  faCircleCheck,
  faCircleXmark,
  faEnvelopeOpenText,
  faPaperPlane,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  StatTile,
  StatTileSkeleton,
  type StatTone,
} from "./beta/stat-tiles";

type WidgetConfig = {
  status: string;
  label: string;
  icon: IconDefinition;
  tone: StatTone;
};

const widgetData: WidgetConfig[] = [
  {
    status: "Pending-Invite",
    label: "Pending Invite",
    icon: faEnvelopeOpenText,
    tone: "orange",
  },
  {
    status: "Inviting",
    label: "Inviting",
    icon: faPaperPlane,
    tone: "indigo",
  },
  {
    status: "Auto-Renewals",
    label: "Auto Renewals",
    icon: faArrowsRotate,
    tone: "sky",
  },
  {
    status: "Payment-Failed",
    label: "Payment Failed",
    icon: faTriangleExclamation,
    tone: "amber",
  },
  {
    status: "Renewed",
    label: "Renewed",
    icon: faCircleCheck,
    tone: "emerald",
  },
  {
    status: "Failed",
    label: "Failed",
    icon: faCircleXmark,
    tone: "rose",
  },
];

export default function TopBarWidget() {
  const user = GetUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [widgetCount, setWidgetCount] = useState<Record<string, any>[]>([]);
  const isMyCustomer = window.location.pathname === "/my-customers";
  const { startDate, endDate } = UseGraph();

  useEffect(() => {
    const findWidgets = async () => {
      setLoading(true);
      const filtersArray = [
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const res = await axiosInstance.get(
          `/stats/chase/counts${formQueryUrl(filtersArray, "?")}`
        );
        setWidgetCount(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    findWidgets();
  }, [user?.code, isMyCustomer, startDate, endDate]);

  const actionedData = ["Cancelled", "Rebroked", "Mismatched"];

  const getCount = (status: string) => {
    if (status === "Actioned") {
      return widgetCount.reduce(
        (prevWC: number, newWC: any) =>
          prevWC + (actionedData?.includes(newWC?.status) ? Number(newWC?.count) : 0),
        0
      );
    }
    return Number(widgetCount.find((w) => w.status === status)?.count) || 0;
  };

  // The whole each tile is a share of — only the statuses shown here.
  const total = widgetData.reduce((sum, item) => sum + getCount(item.status), 0);

  return (
    <div className="mt-6 grid animate-fade-in grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {loading
        ? widgetData.map((item) => <StatTileSkeleton key={item.status} />)
        : widgetData.map((item) => (
            <StatTile
              key={item.status}
              label={item.label}
              value={getCount(item.status)}
              total={total}
              icon={item.icon}
              tone={item.tone}
            />
          ))}
    </div>
  );
}
